import React, { useState } from "react";
import TimeDropdownSS from "./TimeDropdownSS";
import { X } from "lucide-react";
import { timeOptions } from "./timeOptions";
import EmotionDropdown from "./EmotionsSelector";
import ThresholdDropdown from "./ThresholdDropdown";
import { handleMultiEmotion } from "../../../StudentData/handlers/multiEmotionHandler";
import { alertTypeOptions, alertStatusOptions } from "../../utils";
import CombinedAlertDropdown from "./combinedAlertDropdown";
import NoData from "../../components/NoData";

const FiltersPanel = ({
  state,
  updateState,
  isOpen,
  onClose,
  filteredStudentsCount,
}) => {
  if (!isOpen) return null;
  const clearTimeFilter = () => {
    updateState({ chosenTime: "" });
  };

  const selectedEmotions = state.multiEmotion.emotions || [];
  const clearEmotion = (e, emotion) => {
    e.stopPropagation();
    const newSelection = selectedEmotions.filter((e) => e !== emotion);
    const status = newSelection.length > 0;
    updateState({ multiEmotion: { status, emotions: newSelection } });
    updateState(handleMultiEmotion(state, newSelection));
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    updateState({ chosenPercentage: "" });
  };

  const clearAlertSelection = (e) => {
    e.stopPropagation();
    const newState = { ...state, chosenAlertStatus: "", chosenAlertType: "" };
    updateState(newState);
  };

  return (
    <div className="fixed inset-0 bg-black/20 z-[1001]">
      <div className="flex flex-col fixed top-0 left-0 sm:left-[130px] h-screen w-full sm:w-[40vw] lg:w-[20vw] bg-white shadow z-50 overflow-y-auto px-3 pb-5 border-r border-[#DDDDDD]">
        <div className="flex justify-between items-center sticky top-0 z-10 bg-white pt-3 pb-3">
          <h2 className="text-[18px] text-700 font-bold">Filters</h2>
          <button onClick={onClose}>
            <X className="text-[#909AA5] w-5 h-5" />
          </button>
        </div>

        <div className="p-3 rounded border border-[#DDDDDD] mb-3">
          {/* Student Data Filters */}
          <h3 className="text-[14px] text-700 font-bold opacity-75 mb-3">
            STUDENT DATA FILTERS
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-[14px] font-bold text-600 mb-3">
                Emotion
              </label>
              <EmotionDropdown
                state={state}
                updateState={updateState}
                selectedEmotions={selectedEmotions}
                clearEmotion={clearEmotion}
              />
            </div>
            <div>
              <label className="block text-[14px] font-bold text-600 mb-3">
                Threshold
              </label>
              <ThresholdDropdown
                state={state}
                updateState={updateState}
                clearSelection={clearSelection}
              />
            </div>
            <div>
              <label className="block text-[14px] font-bold text-600 mb-3">
                Time Period
              </label>
              <TimeDropdownSS
                state={state}
                updateState={updateState}
                clearSelection={clearTimeFilter}
              />
            </div>
          </div>
        </div>
        <div className="p-3 space-y-3 mt-10 rounded border border-[#DDDDDD] mb-3">
          <h3 className="text-[14px] text-700 font-bold opacity-75 mb-3">
            ALERT FILTERS
          </h3>
          <div className="space-y-3">
            <div>
              <CombinedAlertDropdown
                state={state}
                updateState={updateState}
                clearSelection={clearAlertSelection}
              />
            </div>
          </div>
        </div>
        <h4 className="text-[14px] mt-5 font-bold text-500 mb-2.5">Filters:</h4>
        <div className="flex flex-wrap gap-2 mb-2.5">
          <div className="flex flex-wrap gap-2">
            {selectedEmotions.length === 0 ? (
              <div className="flex items-center gap-2 bg-blue-100 text-[#2763D4] text-[14px] px-2 py-1 rounded-full">
                <span>Any emotion</span>
                <button
                  className="text-[#2763D4]"
                  onClick={() => {
                    updateState({
                      ...state,
                      multiEmotion: { status: false, emotions: [] },
                    });
                    updateState(handleMultiEmotion(state, []));
                  }}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              selectedEmotions.map((emotion) => (
                <div
                  key={emotion}
                  className="flex items-center gap-2 bg-blue-100 text-[#2763D4] text-[14px] px-2 py-1 rounded-full capitalize"
                >
                  <span>{emotion}</span>
                  <button
                    onClick={(e) => clearEmotion(e, emotion)}
                    className="text-[#2763D4]"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="flex">
            <div className="flex items-center bg-blue-100 text-[#2763D4] text-[14px] px-2 py-1 rounded-full">
              {state.chosenPercentage !== ""
                ? `Over ${state.chosenPercentage}%`
                : "Any % above 0"}
              {state.chosenPercentage !== "" && (
                <button
                  onClick={clearSelection}
                  className="ml-2 text-[#2763D4]"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          <div className="flex">
            <div className="flex items-center bg-blue-100 text-[#2763D4] text-[14px] px-2 py-1 rounded-full">
              {state.chosenTime ? (
                <span>
                  {state.chosenTime &&
                    timeOptions.find((o) => o.value === state.chosenTime)
                      ?.display}
                </span>
              ) : (
                <span>All Time</span>
              )}
              <button onClick={clearTimeFilter} className="ml-2 text-[#2763D4]">
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
          <span className="flex items-center bg-blue-100 text-[#2763D4] text-[14px] px-2 py-1 rounded-full">
            {state.chosenAlertType
              ? alertTypeOptions.find((o) => o.value === state.chosenAlertType)
                  ?.label
              : "All Students"}
            {state.chosenAlertType && (
              <button
                onClick={clearAlertSelection}
                className="ml-2 text-[#2763D4]"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
          <span className="flex items-center bg-blue-100 text-[#2763D4] text-[14px] px-2 py-1 rounded-full">
            {state.chosenAlertStatus
              ? alertStatusOptions.find(
                  (o) => o.value === state.chosenAlertStatus
                )?.label
              : "All Students"}
            {state.chosenAlertStatus && (
              <button
                onClick={clearAlertSelection}
                className="ml-2 text-[#2763D4]"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        </div>
        <div className="flex flex-col gap-2">
          {filteredStudentsCount > 0 && (
            <div className="m-auto mt-5 mb-8 xl:mt-10">
              <button
                onClick={onClose}
                className="w-full bg-[#005BE2] text-white p-3 rounded-lg text-sm font-bold hover:bg-[#005BE2]/90 transition"
              >
                Show Students ({filteredStudentsCount})
              </button>
            </div>
          )}
          {filteredStudentsCount === 0 && (
            <div className="sm:hidden">
              <NoData
                hasStudents={state.currentStudents.length > 0}
                hasActivity={filteredStudentsCount}
                chosenStudent={state.chosenStudent}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FiltersPanel;
